import React, { useEffect, useMemo, useRef, useState } from "react";
import { channelPartnerApi, toApiAbsoluteUrl } from "../../api/client";
import { useAuth } from "../../context/AuthContext";

export default function ChannelPartnerProfile() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [profile, setProfile] = useState(null);
  const [investmentCapacity, setInvestmentCapacity] = useState("");
  const [experience, setExperience] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [panNumber, setPanNumber] = useState("");
  const [gstNumber, setGstNumber] = useState("");
  const [address, setAddress] = useState({
    street: "",
    city: "",
    state: "",
    pincode: "",
    country: "India",
  });
  const [files, setFiles] = useState([]);
  const [filePreviews, setFilePreviews] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const signatureInputRef = useRef(null);

  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [gstError, setGstError] = useState("");
  const [panError, setPanError] = useState("");
  const [signatureFile, setSignatureFile] = useState(null);
  const [signaturePreview, setSignaturePreview] = useState(null);
  // UI flags for success/error messages shown after upload

  useEffect(() => {
    (async () => {
      try {
        const res = await channelPartnerApi.getProfile();

        const p = res?.channelPartnerProfile || res?.profile || res || null;

        if (!p) {
          throw new Error("No profile data received");
        }

        setProfile(p);
        setInvestmentCapacity(p?.investmentCapacity || "");
        setExperience(p?.experience || "");
        setCompanyName(p?.companyName || "");
        setPanNumber(p?.panNumber || "");
        setGstNumber(p?.gstNumber || "");
        const addressData = p?.address || {
          street: "",
          city: "",
          state: "",
          pincode: "",
          country: "India",
        };
        setAddress(addressData);

        // Set signature preview if signature exists
        if (Array.isArray(p.signature) && p.signature.length > 0) {
          const signatureUrl = toApiAbsoluteUrl(p.signature[0].url);
          setSignaturePreview(signatureUrl);
        }
      } catch (_err) {
        // Create empty profile with user data if available
        const emptyProfile = {
          name: user?.name || "",
          email: user?.email || "",
          phoneNumber: user?.phoneNumber || "",
          investmentCapacity: "",
          experience: "",
          companyName: "",
          panNumber: "",
          gstNumber: "",
          address: {
            street: "",
            city: "",
            state: "",
            pincode: "",
            country: "India",
          },
          documents: [],
          signature: [],
        };
        setProfile(emptyProfile);
        setInvestmentCapacity("");
        setExperience("");
        setCompanyName("");
        setPanNumber("");
        setGstNumber("");
        setAddress({
          street: "",
          city: "",
          state: "",
          pincode: "",
          country: "India",
        });
        setError("");
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  const isSet = (v) => v !== undefined && v !== null && String(v).trim() !== "";

  const locked = useMemo(() => {
    const p = profile || {};
    return {
      name: isSet(p.name),
      email: isSet(p.email),
      phoneNumber: isSet(p.phoneNumber),
      investmentCapacity: isSet(p.investmentCapacity),
      experience: isSet(p.experience),
      companyName: isSet(p.companyName),
      panNumber: isSet(p.panNumber),
      gstNumber: isSet(p.gstNumber),
      address:
        p.address &&
        (isSet(p.address.street) ||
          isSet(p.address.city) ||
          isSet(p.address.state) ||
          isSet(p.address.pincode)),
      documents: Array.isArray(p.documents) && p.documents.length > 0,
      signature: Array.isArray(p.signature) && p.signature.length > 0,
    };
  }, [profile]);

  // Check if we have signature (either saved or new upload)
  const hasSignature = useMemo(() => {
    return locked.signature || signatureFile !== null;
  }, [locked.signature, signatureFile]);

  // Get current signature display URL
  const currentSignatureUrl = useMemo(() => {
    if (signaturePreview) {
      return signaturePreview;
    }
    if (locked.signature && profile?.signature?.[0]?.url) {
      return toApiAbsoluteUrl(profile.signature[0].url);
    }
    return null;
  }, [signaturePreview, locked.signature, profile]);

  function addFiles(list) {
    const incoming = Array.from(list || []);
    const allowed = ["image/jpeg", "image/png", "application/pdf"];
    const valid = incoming.filter(
      (f) => allowed.includes(f.type) && f.size <= 5 * 1024 * 1024
    );
    const remaining = Math.max(0, 5 - files.length);
    const toAdd = valid.slice(0, remaining);
    if (toAdd.length === 0) return;
    const nextFiles = [...files, ...toAdd];
    const nextPreviews = [...filePreviews];
    toAdd.forEach((f) => {
      if (f.type.startsWith("image/")) {
        try {
          nextPreviews.push(URL.createObjectURL(f));
        } catch {
          nextPreviews.push(null);
        }
      } else {
        nextPreviews.push(null);
      }
    });
    setFiles(nextFiles);
    setFilePreviews(nextPreviews);
  }

  function onFileChange(e) {
    addFiles(e.target.files);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  function removeFile(index) {
    const nextFiles = files.filter((_, i) => i !== index);
    const toRevoke = filePreviews[index];
    if (toRevoke) {
      try {
        URL.revokeObjectURL(toRevoke);
      } catch {}
    }
    const nextPreviews = filePreviews.filter((_, i) => i !== index);
    setFiles(nextFiles);
    setFilePreviews(nextPreviews);
  }

  function formatBytes(bytes) {
    if (!bytes && bytes !== 0) return "";
    const sizes = ["B", "KB", "MB", "GB", "TB"];
    if (bytes === 0) return "0 B";
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    const val = bytes / Math.pow(1024, i);
    return `${val.toFixed(val >= 100 ? 0 : val >= 10 ? 1 : 2)} ${sizes[i]}`;
  }

  function isValidGST(gst) {
    if (!gst) return false;
    const regex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    return regex.test(gst.toUpperCase());
  }

  function isValidPAN(pan) {
    if (!pan) return false;
    const regex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    return regex.test(pan.toUpperCase());
  }

  async function onSubmit(e) {
    e.preventDefault();
    setMessage("");
    setError("");
    setGstError("");
    setPanError("");

    // Validate PAN number
    if (!locked.panNumber && isSet(panNumber) && !isValidPAN(panNumber)) {
      setPanError("Please enter a valid PAN number.");
      return;
    }

    // Validate GST number
    if (!locked.gstNumber && isSet(gstNumber) && !isValidGST(gstNumber)) {
      setGstError("Please enter a valid GST number.");
      return;
    }

    // Validate pincode
    if (!locked.address && address && isSet(address.pincode)) {
      const pincodeRegex = /^[1-9][0-9]{5}$/;
      if (!pincodeRegex.test(address.pincode)) {
        setError("Please enter a valid 6-digit pincode starting with 1-9.");
        return;
      }
    }

    // Validate signature - only require if not already saved
    if (!locked.signature && !signatureFile) {
      setError("Please upload your signature before saving.");
      return;
    }

    try {
      setSubmitting(true);
      const fd = new FormData();

      if (!locked.investmentCapacity && isSet(investmentCapacity))
        fd.append("investmentCapacity", investmentCapacity.trim());
      if (!locked.experience && isSet(experience))
        fd.append("experience", experience.trim());
      if (!locked.companyName && isSet(companyName))
        fd.append("companyName", companyName.trim());
      if (!locked.gstNumber && isSet(gstNumber))
        fd.append("gstNumber", gstNumber.trim());
      if (!locked.panNumber && isSet(panNumber))
        fd.append("panNumber", panNumber.trim());
      if (
        !locked.address &&
        address &&
        (isSet(address.street) ||
          isSet(address.city) ||
          isSet(address.state) ||
          isSet(address.pincode))
      ) {
        fd.append("address[street]", address.street || "");
        fd.append("address[city]", address.city || "");
        fd.append("address[state]", address.state || "");
        fd.append("address[pincode]", address.pincode || "");
        fd.append("address[country]", address.country || "India");
      }
      if (!locked.documents && files.length > 0) {
        files.forEach((f) => fd.append("documents", f));
      }
      if (!locked.signature && signatureFile) {
        fd.append("signature", signatureFile);
      }

      const res = await channelPartnerApi.updateProfile(fd);
      const updatedProfile = res?.channelPartnerProfile || null;
      setProfile(updatedProfile);

      // If server returned a saved signature, use its URL as the preview so it persists after refresh
      const signatureNow = Boolean(
        updatedProfile && Array.isArray(updatedProfile.signature) && updatedProfile.signature.length > 0
      );
      if (signatureNow) {
        setSignaturePreview(toApiAbsoluteUrl(updatedProfile.signature[0].url));
      } else {
        setSignaturePreview(null);
      }
      setInvestmentCapacity(updatedProfile?.investmentCapacity || "");
      setExperience(updatedProfile?.experience || "");
      setCompanyName(updatedProfile?.companyName || "");
      setPanNumber(updatedProfile?.panNumber || "");
      setGstNumber(updatedProfile?.gstNumber || "");
      const newAddressData = updatedProfile?.address || {
        street: "",
        city: "",
        state: "",
        pincode: "",
        country: "India",
      };
      setAddress(newAddressData);
      setFiles([]);

      // Clean up local file object if it was used for upload
      if (signatureFile) {
        if (signaturePreview && signaturePreview.startsWith("blob:")) {
          try {
            URL.revokeObjectURL(signaturePreview);
          } catch {}
        }
        setSignatureFile(null);
      }

      const updated = Array.isArray(res?.updated) ? res.updated : [];
      const label = (k) =>
        ({
          investmentCapacity: "Investment Capacity",
          experience: "Experience",
          companyName: "Company Name",
          gstNumber: "GST Number",
          panNumber: "PAN Number",
          address: "Address",
          documents: "Documents",
          signature: "Signature",
        }[k] || k);
      let updatedMsg = updated.length ? `Updated ${updated.map(label).join(", ")}.` : "No changes were made.";
      if (signatureNow) updatedMsg = "Signature uploaded and saved.";
      if (!updatedMsg) updatedMsg = "Profile updated successfully.";
      setMessage(updatedMsg);
    } catch (err) {
      const data = err?.data;

      if (data?.code === "VALIDATION_ERROR" && data?.errors) {
        // Handle validation errors
        const validationErrors = data.errors
          .map((e) => `${e.field}: ${e.message}`)
          .join(", ");
        setError(`Validation failed: ${validationErrors}`);
      } else {
        const msg =
          data?.errors?.[0]?.message || data?.message || "Update failed";
        setError(msg);
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSubmitProfile() {
    setMessage("");
    setError("");
    setGstError("");
    setPanError("");

    try {
      setSubmitting(true);
      await channelPartnerApi.submitProfile();
      setMessage("Profile submitted for approval successfully!");
      // Refresh profile data
      const profileRes = await channelPartnerApi.getProfile();
      const updatedProfile =
        profileRes?.channelPartnerProfile ||
        profileRes?.profile ||
        profileRes ||
        null;
      if (updatedProfile) {
        setProfile(updatedProfile);
      }
    } catch (err) {
      const data = err?.data;
      const msg =
        data?.errors?.[0]?.message ||
        data?.message ||
        "Failed to submit profile";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  // Clean up object URLs on unmount
  useEffect(() => {
    return () => {
      filePreviews.forEach((url) => {
        if (url && url.startsWith("blob:")) {
          URL.revokeObjectURL(url);
        }
      });
      if (signaturePreview && signaturePreview.startsWith("blob:")) {
        URL.revokeObjectURL(signaturePreview);
      }
    };
  }, []);

  if (loading)
    return <div className="text-center text-gray-600">Loading profile...</div>;
  if (error && !profile)
    return <div className="text-center text-red-600">{error}</div>;
  if (!profile)
    return <div className="text-center text-gray-600">Profile not found</div>;

  return (
    <div className="max-w-3xl mx-auto">
      <h2 className="text-2xl font-bold mb-6 text-gray-800">
        Channel Partner Profile
      </h2>

      {message && (
        <div className="mb-4 p-3 rounded bg-green-50 text-green-700 border border-green-200">
          {message}
        </div>
      )}
      {error && (
        <div className="mb-4 p-3 rounded bg-red-50 text-red-700 border border-red-200">
          {error}
        </div>
      )}

      <form onSubmit={onSubmit} className="space-y-6">
        {/* Basic Information - Pre-filled and locked */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-semibold uppercase text-gray-600 mb-1">
              Name
            </label>
            <input
              type="text"
              value={profile.name || ""}
              disabled
              className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600"
            />
            <p className="text-xs text-gray-500 mt-1">
              Set during account creation and cannot be changed.
            </p>
          </div>
          <div>
            <label className="block text-sm font-semibold uppercase text-gray-600 mb-1">
              Email
            </label>
            <input
              type="email"
              value={profile.email || ""}
              disabled
              className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600"
            />
            <p className="text-xs text-gray-500 mt-1">
              Set during account creation and cannot be changed.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-semibold uppercase text-gray-600 mb-1">
              Phone Number
            </label>
            <input
              type="text"
              value={profile.phoneNumber || ""}
              disabled
              className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600"
            />
            <p className="text-xs text-gray-500 mt-1">
              Set during account creation and cannot be changed.
            </p>
          </div>
          <div>
            <label className="block text-sm font-semibold uppercase text-gray-600 mb-1">
              Investment Capacity (₹)
            </label>
            <input
              type="number"
              placeholder="Enter investment capacity"
              value={investmentCapacity}
              onChange={(e) => setInvestmentCapacity(e.target.value)}
              disabled={locked.investmentCapacity}
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                locked.investmentCapacity
                  ? "bg-gray-100 text-gray-600 border-gray-300"
                  : "border-gray-300 focus:ring-blue-400"
              }`}
            />
            <p
              className={`text-xs mt-1 ${
                locked.investmentCapacity ? "text-gray-500" : "text-gray-400"
              }`}
            >
              {locked.investmentCapacity
                ? "Already set and locked."
                : "You can set this once."}
            </p>
          </div>
        </div>

        {/* Business Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-semibold uppercase text-gray-600 mb-1">
              Experience (Years)
            </label>
            <input
              type="number"
              placeholder="Enter years of experience"
              value={experience}
              onChange={(e) => setExperience(e.target.value)}
              disabled={locked.experience}
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                locked.experience
                  ? "bg-gray-100 text-gray-600 border-gray-300"
                  : "border-gray-300 focus:ring-blue-400"
              }`}
            />
            <p
              className={`text-xs mt-1 ${
                locked.experience ? "text-gray-500" : "text-gray-400"
              }`}
            >
              {locked.experience
                ? "Already set and locked."
                : "You can set this once."}
            </p>
          </div>
          <div>
            <label className="block text-sm font-semibold uppercase text-gray-600 mb-1">
              Company Name
            </label>
            <input
              type="text"
              placeholder="Enter company name"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              disabled={locked.companyName}
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                locked.companyName
                  ? "bg-gray-100 text-gray-600 border-gray-300"
                  : "border-gray-300 focus:ring-blue-400"
              }`}
            />
            <p
              className={`text-xs mt-1 ${
                locked.companyName ? "text-gray-500" : "text-gray-400"
              }`}
            >
              {locked.companyName
                ? "Already set and locked."
                : "You can set this once."}
            </p>
          </div>
        </div>

        {/* Address Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-semibold uppercase text-gray-600 mb-1">
              Street Address
            </label>
            <input
              type="text"
              placeholder="Enter street address"
              value={address.street || ""}
              onChange={(e) =>
                setAddress({ ...address, street: e.target.value })
              }
              disabled={locked.address}
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                locked.address
                  ? "bg-gray-100 text-gray-600 border-gray-300"
                  : "border-gray-300 focus:ring-blue-400"
              }`}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold uppercase text-gray-600 mb-1">
              City
            </label>
            <input
              type="text"
              placeholder="Enter city"
              value={address.city || ""}
              onChange={(e) => setAddress({ ...address, city: e.target.value })}
              disabled={locked.address}
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                locked.address
                  ? "bg-gray-100 text-gray-600 border-gray-300"
                  : "border-gray-300 focus:ring-blue-400"
              }`}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-semibold uppercase text-gray-600 mb-1">
              State
            </label>
            <input
              type="text"
              placeholder="Enter state"
              value={address.state || ""}
              onChange={(e) =>
                setAddress({ ...address, state: e.target.value })
              }
              disabled={locked.address}
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                locked.address
                  ? "bg-gray-100 text-gray-600 border-gray-300"
                  : "border-gray-300 focus:ring-blue-400"
              }`}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold uppercase text-gray-600 mb-1">
              Pincode
            </label>
            <input
              type="text"
              placeholder="Enter pincode"
              value={address.pincode || ""}
              onChange={(e) =>
                setAddress({ ...address, pincode: e.target.value })
              }
              disabled={locked.address}
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                locked.address
                  ? "bg-gray-100 text-gray-600 border-gray-300"
                  : "border-gray-300 focus:ring-blue-400"
              }`}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold uppercase text-gray-600 mb-1">
              Country
            </label>
            <input
              type="text"
              value={address.country || "India"}
              disabled
              className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600"
            />
          </div>
        </div>

        {/* Tax Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-semibold uppercase text-gray-600 mb-1">
              GST Number
            </label>
            <input
              type="text"
              placeholder="Enter GST number"
              value={gstNumber}
              onChange={(e) => setGstNumber(e.target.value.toUpperCase())}
              disabled={locked.gstNumber}
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                locked.gstNumber
                  ? "bg-gray-100 text-gray-600 border-gray-300"
                  : "border-gray-300 focus:ring-blue-400"
              }`}
            />
            <p
              className={`text-xs mt-1 ${
                gstError
                  ? "text-red-500"
                  : gstNumber && !locked.gstNumber && !isValidGST(gstNumber)
                  ? "text-red-500"
                  : locked.gstNumber
                  ? "text-gray-500"
                  : "text-gray-400"
              }`}
            >
              {gstError
                ? gstError
                : gstNumber && !locked.gstNumber && !isValidGST(gstNumber)
                ? "Invalid GST number format."
                : locked.gstNumber
                ? "Already set and locked."
                : "You can set this once."}
            </p>
          </div>
          <div>
            <label className="block text-sm font-semibold uppercase text-gray-600 mb-1">
              PAN Number
            </label>
            <input
              type="text"
              placeholder="Enter PAN number"
              value={panNumber}
              onChange={(e) => setPanNumber(e.target.value.toUpperCase())}
              disabled={locked.panNumber}
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                locked.panNumber
                  ? "bg-gray-100 text-gray-600 border-gray-300"
                  : "border-gray-300 focus:ring-blue-400"
              }`}
            />
            <p
              className={`text-xs mt-1 ${
                panError
                  ? "text-red-500"
                  : panNumber && !locked.panNumber && !isValidPAN(panNumber)
                  ? "text-red-500"
                  : locked.panNumber
                  ? "text-gray-500"
                  : "text-gray-400"
              }`}
            >
              {panError
                ? panError
                : panNumber && !locked.panNumber && !isValidPAN(panNumber)
                ? "Invalid PAN number format."
                : locked.panNumber
                ? "Already set and locked."
                : "You can set this once."}
            </p>
          </div>
        </div>

        {/* Signature Upload */}
        <div className="w-1/2">
          <label className="block text-base font-semibold uppercase text-gray-600 mb-1">
            Signature
          </label>

          {hasSignature && currentSignatureUrl ? (
            <div className="p-1 border border-gray-200 rounded-lg bg-gray-50">
              <div className="flex items-center gap-4">
                <img
                  src={currentSignatureUrl}
                  alt="Signature"
                  className="h-20 object-contain border rounded-lg"
                />
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900">
                    {locked.signature
                      ? profile?.signature?.[0]?.originalName || "Saved Signature"
                      : signatureFile?.name || "New Signature Preview"}
                  </div>
                  <div
                    className={`text-xs mt-1 ${
                      locked.signature ? "text-green-600" : "text-blue-600"
                    }`}
                  >
                    {locked.signature
                      ? "Signature saved and locked"
                      : "New signature ready to save"}
                  </div>
                  {/* Show filename and uploaded date for saved signature; for new signature show size */}
                  {locked.signature && profile?.signature?.[0]?.uploadedAt && (
                    <div className="text-xs text-gray-500 mt-1">
                      Uploaded on:{" "}
                      {new Date(
                        profile.signature[0].uploadedAt
                      ).toLocaleDateString()}
                    </div>
                  )}
                  {!locked.signature && signatureFile && (
                    <div className="text-xs text-gray-500 mt-1">
                      {signatureFile.name} • {formatBytes(signatureFile.size)}
                    </div>
                  )}
                  {locked.signature && (
                    <div className="mt-2">
                      <a
                        href={currentSignatureUrl}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                      >
                        View full signature
                      </a>
                    </div>
                  )}
                </div>
              </div>

              {/* Only show remove button if not locked and we have a new signature file */}
              {!locked.signature && signatureFile && (
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={() => {
                      if (
                        signaturePreview &&
                        signaturePreview.startsWith("blob:")
                      ) {
                        URL.revokeObjectURL(signaturePreview);
                      }
                      setSignatureFile(null);
                      setSignaturePreview(null);
                    }}
                    className="text-xs px-3 py-1 rounded bg-red-50 text-red-700 hover:bg-red-100 border border-red-200"
                  >
                    Remove New Signature
                  </button>
                  <p className="text-xs text-gray-500 mt-1">
                    Click "Save Changes" to permanently save this signature
                  </p>
                </div>
              )}
            </div>
          ) : (
            <>
              <div
                onClick={() => signatureInputRef.current?.click()}
                className="mt-1 w-1/4 flex flex-col items-center justify-center gap-2 px-2 py-2 rounded-lg border-4 border-collapse cursor-pointer border-gray-300 bg-gray-50 hover:bg-gray-100"
              >
                <div className="text-sm text-gray-700">Upload signature</div>
                <div className="text-xs text-gray-500">
                  Only JPG or PNG, max 2MB
                </div>
                <input
                  ref={signatureInputRef}
                  type="file"
                  accept="image/png,image/jpeg"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      if (file.size <= 2 * 1024 * 1024) {
                        setSignatureFile(file);
                        setSignaturePreview(URL.createObjectURL(file));
                      } else {
                        alert("File size must be less than 2MB");
                      }
                    }
                  }}
                />
              </div>
              {locked.signature && (
                <p className="text-xs text-gray-500 mt-1">
                  Signature already uploaded and cannot be changed
                </p>
              )}
            </>
          )}
        </div>

        {/* Documents Upload */}
        <div>
          <label className="block text-sm font-semibold uppercase text-gray-600 mb-1">
            Documents
          </label>
          {locked.documents ? (
            <div className="p-4 border border-gray-200 rounded-lg bg-gray-50 text-gray-600">
              Documents already uploaded and locked.
            </div>
          ) : (
            <>
              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setIsDragging(false);
                  addFiles(e.dataTransfer.files);
                }}
                onClick={() => fileInputRef.current?.click()}
                className={`mt-1 w-full flex flex-col items-center justify-center gap-2 px-4 py-6 rounded-lg border-2 border-dashed cursor-pointer ${
                  isDragging
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-300 bg-gray-50 hover:bg-gray-100"
                }`}
              >
                <div className="text-sm text-gray-700">
                  Drag & drop files here, or click to browse
                </div>
                <div className="text-xs text-gray-500">
                  Up to 5 files (JPG/PNG/PDF), max 5MB each. You can upload
                  once.
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/png,image/jpeg,application/pdf"
                  className="hidden"
                  onChange={onFileChange}
                />
              </div>
              <div className="mt-3 space-y-2">
                {files.length === 0 && (
                  <div className="text-xs text-gray-500">No files selected</div>
                )}
                {files.map((file, i) => (
                  <div
                    key={`${file.name}-${i}`}
                    className="flex items-center gap-3 p-2 border rounded bg-white"
                  >
                    <div className="h-12 w-12 rounded overflow-hidden bg-gray-100 flex items-center justify-center">
                      {file.type.startsWith("image/") && filePreviews[i] ? (
                        <img
                          src={filePreviews[i]}
                          alt={file.name}
                          className="object-cover h-full w-full"
                        />
                      ) : (
                        <span className="text-[10px] text-gray-500">
                          {file.type.includes("pdf") ? "PDF" : "FILE"}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div
                        className="text-sm text-gray-800 truncate"
                        title={file.name}
                      >
                        {file.name}
                      </div>
                      <div className="text-xs text-gray-500">
                        {formatBytes(file.size)}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(i)}
                      className="text-xs px-2 py-1 rounded bg-red-50 text-red-700 hover:bg-red-100"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="flex justify-between items-center">
          <div>
            {profile?.status === "incomplete" && (
              <div className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg border border-amber-200">
                <p>
                  <strong>Profile Incomplete:</strong> Complete all fields and
                  submit for approval to create promoters.
                </p>
              </div>
            )}
            {profile?.status === "pending" && (
              <div className="text-sm text-blue-600 bg-blue-50 p-3 rounded-lg border border-blue-200">
                <p>
                  <strong>Profile Pending:</strong> Your profile is under
                  review. You'll be able to create promoters once approved.
                </p>
              </div>
            )}
            {profile?.status === "approved" && (
              <div className="text-sm text-green-600 bg-green-50 p-3 rounded-lg border border-green-200">
                <p>
                  <strong>Profile Approved:</strong> You can now create
                  promoters!
                </p>
              </div>
            )}
          </div>

          <div className="flex gap-3">
            {profile?.status === "incomplete" && (
              <button
                type="button"
                onClick={handleSubmitProfile}
                disabled={submitting}
                className="bg-gradient-to-r from-green-500 to-green-700 uppercase px-6 py-2 text-white rounded-lg mulish-bold transition hover:text-green-200"
              >
                Submit for Approval
              </button>
            )}
            <button
              type="submit"
              disabled={submitting}
              className={`bg-gradient-to-r from-blue-500 to-blue-700 uppercase px-6 py-2 text-white rounded-lg mulish-bold transition ${
                submitting
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:text-blue-200"
              }`}
            >
              {submitting ? (
                <span className="flex items-center gap-2">
                  <svg
                    className="animate-spin h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    ></path>
                  </svg>
                  Saving...
                </span>
              ) : (
                "Save Changes"
              )}
            </button>
          </div>
        </div>
      </form>

      {/* Existing documents preview */}
      <div className="mt-10">
        <h3 className="text-xl font-semibold text-gray-800 mb-3">
          Uploaded Documents
        </h3>
        {(() => {
          return (
            Array.isArray(profile.documents) && profile.documents.length > 0
          );
        })() ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {profile.documents.map((doc) => {
              const isImage = (doc?.mimeType || "").startsWith("image/");
              const isPdf = (doc?.mimeType || "").includes("pdf");
              const absUrl = toApiAbsoluteUrl(doc?.url);
              return (
                <div
                  key={doc.id || doc._id || doc.url}
                  className="group bg-white rounded-2xl shadow-md ring-1 ring-gray-100 overflow-hidden hover:shadow-lg transition"
                >
                  <div className="relative">
                    {isImage ? (
                      <img
                        src={absUrl}
                        alt={doc?.originalName || "document"}
                        className="h-40 w-full object-cover"
                        loading="lazy"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = `https://placehold.co/600x300/png?text=${encodeURIComponent(
                            doc?.originalName || "Document"
                          )}`;
                        }}
                      />
                    ) : (
                      <div className="h-40 w-full flex items-center justify-center bg-gray-50 text-gray-500">
                        <span className="text-sm font-medium">
                          {isPdf ? "PDF Document" : doc?.mimeType || "File"}
                        </span>
                      </div>
                    )}
                    <div className="absolute top-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                      {formatBytes(doc?.size)}
                    </div>
                  </div>
                  <div className="p-3">
                    <div
                      className="text-sm font-medium text-gray-900 truncate"
                      title={doc?.originalName}
                    >
                      {doc?.originalName || "Document"}
                    </div>
                    <div
                      className="text-xs text-gray-500 mt-1 truncate"
                      title={doc?.mimeType}
                    >
                      {doc?.mimeType}
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      <a
                        href={absUrl}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                      >
                        View
                      </a>
                      {doc?.uploadedAt && (
                        <span className="text-[10px] text-gray-400">
                          {new Date(doc.uploadedAt).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-gray-500">No documents uploaded yet.</div>
        )}
      </div>
    </div>
  );
}
